import Vue from "vue";
import Router from "vue-router";
import { UserToken } from "@/api/tokens";
import { ENV } from "./appConfig";

Vue.use(Router);

const EmptyRoute = Vue.component("EmptyRoute", {
  template: "<router-view/>"
});

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    const contentView = document.querySelector(".authLayout__main__content");
    if (contentView) {
      contentView.scrollTo(0, 0);
    }
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: "/session",
      name: "session",
      component: () => import("@/views/Session.vue")
    },
    {
      path: "",
      component: () => import("@/layouts/LoginLayout.vue"),
      beforeEnter: (to, from, next) => {
        if (UserToken.get()) {
          router.push({
            name: "authSummary"
          });
          next(false);
        }
        return next();
      },
      children: [
        {
          path: "/",
          name: "index",
          component: () => import("@/views/Login.vue")
        },
        {
          path: "/login",
          name: "login",
          component: () => import("@/views/Login.vue")
        },
        {
          path: "/forgotPassword",
          name: "forgotPassword",
          component: () => import("@/views/ForgotPassword.vue")
        },
        {
          path: "/resetPassword",
          name: "resetPassword",
          component: () => import("@/views/ResetPassword.vue")
        },
        {
          path: "/confirmUser",
          name: "confirmUser",
          component: () => import("@/views/ConfirmUser.vue")
        },
        {
          path: "/invalidPermission",
          name: "invalidPermission",
          component: () => import("./views/InvalidPermission.vue")
        }
      ]
    },
    {
      path: "/sessionEnded",
      name: "sessionEnded",
      component: () => import("./views/SessionEnded.vue")
    },
    {
      path: "/",
      component: () => import("@/layouts/DefaultOrAuthLayout.vue"),
      children: [
        {
          path: "",
          redirect: "contact"
        },
        {
          path: "/contact",
          name: "contact",
          component: () => import("./views/Contact.vue")
        },
        {
          path: "/faqs",
          name: "faqs",
          component: () => import("./views/Faqs.vue")
        },
        {
          path: "/privacy",
          name: "privacy",
          component: () => import("./views/Privacy.vue")
        },
        {
          path: "/terms",
          name: "terms",
          component: () => import("./views/TermsAndConditions.vue")
        }
      ]
    },
    {
      path: "/auth",
      component: () => import("./layouts/auth/AuthLayout.vue"),
      children: [
        {
          path: "",
          redirect: "dashboard"
        },
        {
          path: "adminTools",
          name: "adminTools",
          redirect: "adminToolsStationDisconnects",
          component: EmptyRoute,
          children: [
            {
              path: "reporting",
              name: "adminToolsReporting",
              component: () => import("./views/auth/adminTools/Reporting.vue"),
              children: [
                {
                  path: "propertyTypes",
                  name: "adminToolsReportingPropertyTypes",
                  component: () => import("./views/auth/adminTools/reporting/ReportingTools.vue")
                },
                {
                  path: "creditPrograms",
                  name: "adminToolsReportingPropertyTypesCreditPrograms",
                  component: () => import("./views/auth/adminTools/reporting/CreditProgram.vue")
                },
                {
                  path: "topProperties",
                  name: "adminToolsReportingTopProperties",
                  component: () => import("./views/auth/adminTools/reporting/TopProperties.vue")
                },
                {
                  path: "pricingHistory",
                  name: "adminToolsReportingPricingHistory",
                  component: () => import("./views/auth/adminTools/reporting/PricingHistory.vue")
                },
                {
                  path: "stationUptime",
                  name: "adminToolsReportingStationUptime",
                  component: () => import("./views/auth/adminTools/reporting/StationUptime.vue")
                }
              ]
            },
            {
              path: "notifications",
              name: "adminToolsNotifications",
              component: () => import("./views/auth/adminTools/Notifications.vue")
            },
            {
              path: "auditHistory",
              name: "adminToolsAuditHistory",
              component: () => import("./views/auth/adminTools/AuditHistory.vue")
            },
            {
              path: "adr",
              name: "adminToolsADREvents",
              component: () => import("./views/auth/adminTools/ADREvents.vue")
            },
            {
              path: "org-management",
              name: "adminToolsOrgManagement",
              component: () => import("./views/auth/adminTools/OrgManagement.vue"),
              children: [
                {
                  path: "organization/:id",
                  name: "adminToolsOrgManagementOrganization",
                  component: () => import("./views/auth/adminTools/orgManagement/Organization.vue")
                },
                {
                  path: "property/:id",
                  name: "adminToolsOrgManagementProperty",
                  component: () => import("./views/auth/adminTools/orgManagement/Property.vue")
                }
              ]
            },
            {
              path: "user-management",
              name: "adminToolsUserManagement",
              component: () => import("./views/auth/adminTools/UserManagement.vue"),
              children: [
                {
                  path: "details/:id",
                  name: "adminToolsUserManagementUserDetails",
                  component: () => import("./views/auth/adminTools/userManagement/Details.vue")
                }
              ]
            },
            {
              path: "station-disconnects",
              name: "adminToolsStationDisconnects",
              component: () => import("./views/auth/adminTools/StationDisconnects.vue"),
              children: [
                {
                  path: "history",
                  name: "adminToolsStationDisconnectsHistory",
                  component: () => import("./views/auth/adminTools/stationDisconnects/History.vue")
                },
                {
                  path: "top-disconnects",
                  name: "adminToolsStationDisconnectsTop",
                  component: () => import("./views/auth/adminTools/stationDisconnects/TopDisconnects.vue")
                },
                {
                  path: "group-disconnects",
                  name: "adminToolsStationDisconnectsGroup",
                  component: () => import("./views/auth/adminTools/stationDisconnects/GroupDisconnects.vue")
                }
              ]
            },
            {
              path: "unregistered-stations",
              name: "adminToolsUnregisteredStations",
              component: () => import("./views/auth/adminTools/UnregisteredStations.vue")
            },
            {
              path: "metrics",
              name: "adminToolsMetrics",
              component: () => import("./views/auth/adminTools/Metrics.vue")
            }
          ].filter(path => ENV !== "gov" || (ENV === "gov" && path.path !== "reporting"))
        },
        {
          path: "summary",
          name: "authSummary",
          component: () => import("./views/auth/Summary.vue")
        },
        {
          path: "settings",
          name: "authSettings",
          component: () => import("./views/auth/Settings.vue")
        },
        {
          path: "system-health",
          name: "systemHealth",
          component: () => import("./views/auth/SystemHealth.vue")
        },
        {
          path: "stationMap",
          name: "authStationMap",
          component: () => import("./views/auth/StationMap.vue")
        },
        {
          path: "network",
          name: "authNetwork",
          component: () => import("./views/auth/Network.vue")
        },
        {
          path: "station/id/:id",
          name: "authViewStationById",
          component: () => import("./views/auth/StationView.vue")
        },
        {
          path: "station/serial/:serial",
          name: "authViewStationBySerial",
          component: () => import("./views/auth/StationView.vue")
        },
        {
          path: "*",
          redirect: "summary"
        }
      ]
    },
    {
      path: "/404",
      name: "404",
      component: () => import("@/views/404.vue")
    },
    {
      path: "*",
      redirect: "/404"
    }
  ]
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

export default router;
